import type { QueryClient } from '@tanstack/react-query';
import type { AssistantObjectRef, AssistantObjectType } from '@contracts/assistant';
import type { BatchRowKind } from '@contracts/ingest';

/**
 * The queries a step's objects make stale (D128). Prefixes: `['workplace']` covers the board, the trees and the done
 * list (`WorkplacePage.tsx` invalidates the same). The board's realtime stream would refresh it too; the explicit call
 * lets the glow land on the new card without waiting for the stream. P3 adds the other types (D168).
 */
export const REFRESH_KEYS: Record<AssistantObjectType, readonly (readonly unknown[])[]> = {
  // S9: a confirmed delegation write (progress / respond / decide / track), a reassign or a deadline move reports a
  // task; a demote reports a note and stops tracking. Both refresh the Giao việc block and the two delegation lists.
  task: [['workplace'], ['dashboard'], ['task'], ['project'], ['partner'], ['strategy'], ['delegation'], ['delegations']],
  note: [['workplace'], ['dashboard'], ['task'], ['project'], ['partner'], ['strategy'], ['delegation'], ['delegations']],
  project: [['projects'], ['project'], ['finance', 'project']],
  partner: [['partners'], ['partner']],
  objective: [['strategy']], priority: [['strategy']], user: [['users']],
  // S5 §6.2: every finance key starts with 'finance' (finance/api.ts); a stakeholder, its people and contracts sit
  // under the project's key (`stakeholdersKey`); a contact on its partner's page; a metric or a review on strategy's.
  finance_entry: [['finance']],
  stakeholder: [['project']], participant: [['project']], contract: [['project']],
  contact: [['partner']],
  metric: [['strategy']], review: [['strategy']],
  // L2 P27: a document has no page of its own to refresh; it opens through the download POST.
  document: [],
};

export async function refreshFor(queryClient: QueryClient, refs: readonly AssistantObjectRef[]): Promise<void> {
  const keys = new Map<string, readonly unknown[]>();
  for (const r of refs) for (const k of REFRESH_KEYS[r.type]) keys.set(JSON.stringify(k), k);
  await Promise.all([...keys.values()].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

/** The object type a batch row of this kind writes (`record_sources` names a metric value's record `metric`). */
export const watchType = (kind: BatchRowKind): AssistantObjectType => (kind === 'metric_value' ? 'metric' : kind);

/** S5 §8.5: an applied (or undone) batch makes stale the pages of every kind it wrote, as a step's objects do. */
export function refreshKinds(queryClient: QueryClient, kinds: readonly BatchRowKind[]): Promise<void> {
  return refreshFor(queryClient, kinds.map((k) => ({ type: watchType(k), id: '' })));
}
